import React, { useEffect, useState, useRef, useCallback, Suspense } from 'react';
import { UserList, UserFilterInput, PagingInput, UserSortByTerm, SortOrder, User } from '@bechtel/types';
import { find, get, isEqual, map } from 'lodash';
import { GET_USERS } from '@bechtel/client-gql';
import { userDrawerDefaultValues } from '../form-state';
import {
  UserListContainerStyled,
  SearchFieldStyled,
  SearchFieldComponentStyled,
  SearchCanvasButtonStyled,
  StyledIcon,
  UserItemStyled,
  ListStyled,
  ButtonBlock,
  NavLinkStyled,
} from './styled';
import {
  AvatarSize,
  Box,
  ButtonAppearance,
  ButtonSize,
  IconSize,
  Text,
  TextFieldSize,
  useModalState,
  Spinner,
  SpinnerSize,
  Heading,
} from '@bechtel/gds';
import {
  ErrorBoundary,
  ErrorRetry,
  useName,
  CacheKeys,
  useSessionStorage,
  useDebounce,
  PersonaMessages,
} from '@bechtel/client-common';
import { useLingui } from '@lingui/react';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { UserDrawerValuesProps } from './drawer/types';
import { AvatarRegularStyled } from '../styled';
import { UserSearchDrawer } from './drawer';
import { t, Trans } from '@lingui/macro';
import { ProjectAdminMenuItem, ProjectAdminMenuType } from '~/list/types';
import { useProjectAdminMenu } from '~/list/hooks';

export const UserItemList = () => {
  const { getFullName } = useName();
  const { i18n } = useLingui();
  const { getProjectAdminMenuItems } = useProjectAdminMenu();
  const { close, toggle, visible } = useModalState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debounceSearchQuery = useDebounce(searchQuery, 500);
  const persistedDrawerValues = useRef(userDrawerDefaultValues);
  const [drawerValues, setDrawerValues] = useSessionStorage<UserDrawerValuesProps>(
    CacheKeys.UserListDrawerValues,
    persistedDrawerValues.current || userDrawerDefaultValues
  );
  const usersLimit = 20;
  const [loadUsers, { called, loading, error, data, refetch, networkStatus, fetchMore }] = useLazyQuery<
    { users: UserList },
    {
      paging: PagingInput;
      input: UserFilterInput;
    }
  >(GET_USERS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const getUsers = () => {
    loadUsers({
      variables: {
        paging: {
          cursor: '1',
          limit: usersLimit,
        },
        input: {
          searchKeyword: debounceSearchQuery,
          sortBy: drawerValues?.sortBy ? drawerValues?.sortBy : UserSortByTerm.LAST_NAME,
          sortOrder: drawerValues?.sortOrder ? drawerValues?.sortOrder : SortOrder.ASC,
          filterByCompanyIds: map(drawerValues?.companies, (item) => item.id),
          filterByFacilityIds: map(drawerValues?.facilities, (item) => item.id),
          filterByRoleIds: map(drawerValues?.roles, (item) => item.id),
        },
      },
    });
  };
  useEffect(() => {
    if (!location || !location.search || location.search == '') return;
    const params = new URLSearchParams(document.location.search.substring(1));
    const isRefreshList = params.get('refreshList');
    if (isRefreshList) {
      getUsers();
    }
  }, [location, location.search]);

  useEffect(() => {
    if (debounceSearchQuery.length > 0 && debounceSearchQuery.length < 3) return;
    getUsers();
  }, [debounceSearchQuery, drawerValues, loadUsers]);

  const userList: UserList['items'] = get(data, 'users.items', []);
  const totalRecords = get(data, 'users.total', 0);
  const totalPages = Math.ceil(get(data, 'users.total', 1) / usersLimit);
  const userListCursor = get(data, 'users.cursor', 0);
  const prjUserAdminMenuItem = find(getProjectAdminMenuItems(), (item: ProjectAdminMenuItem) => {
    return item.type == ProjectAdminMenuType.USER_ADMIN;
  });
  const onDrawerSubmit = useCallback(
    (values: UserDrawerValuesProps) => {
      setDrawerValues(() => {
        persistedDrawerValues.current = values;
        return values;
      });
    },
    [setDrawerValues]
  );

  const handlScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      userListCursor <= totalPages &&
      !loading
    ) {
      fetchMore({
        variables: {
          paging: {
            cursor: userListCursor.toString(),
            limit: usersLimit,
          },
        },
      });
    }
  };

  return (
    <>
      <UserListContainerStyled isDrawerOpen={visible}>
        <Box
          px={{ sm: '550' }}
          pt={{ sm: '700' }}
          pb={{ sm: '600' }}
          justifyContent={{ sm: 'space-between' }}
          alignItems={{ sm: 'center' }}
        >
          <Text size="600" family="graphik" weight="600" data-testid="total-users">
            {totalRecords} {t`Total Users`}
          </Text>
          <ButtonBlock to={`/project-admin/${prjUserAdminMenuItem?.value}/user/create`}>
            <Text size="400" weight="600">
              + <Trans>New User</Trans>
            </Text>
          </ButtonBlock>
        </Box>
        <SearchFieldStyled>
          <SearchFieldComponentStyled
            size={TextFieldSize.LARGE}
            iconSize={IconSize.MEDIUM}
            placeholder={t`Search by name or BUN...`}
            type="search"
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            value={searchQuery}
          />
          <Box
            ml={{ sm: '450' }}
            css={`
              max-width: ${({ theme }) => theme.spacings[800]};
            `}
          >
            <SearchCanvasButtonStyled
              isDefaultFilterChange={!isEqual(drawerValues, userDrawerDefaultValues)}
              onClick={toggle}
              appearance={ButtonAppearance.SECONDARY}
              size={ButtonSize.LARGE}
            >
              <StyledIcon
                icon="sliders-v"
                size={IconSize.LARGE}
                fillColor={!isEqual(drawerValues, userDrawerDefaultValues) ? 'white' : ''}
              />
            </SearchCanvasButtonStyled>
          </Box>
        </SearchFieldStyled>

        <ListStyled
          onScroll={(e) => {
            handlScroll(e);
          }}
        >
          {map(userList, (userItem: User) => {
            const imageUrl = get(userItem, 'crewMember.imageUrl');
            const crewNumber = userItem.crewMember?.crew?.crewNumber;
            const encodedId = encodeURIComponent(userItem.id);
            return (
              <NavLinkStyled
                data-testid={`user-list-item-${encodedId}`}
                to={`/project-admin/${prjUserAdminMenuItem?.value}/${encodedId}`}
                key={encodedId}
              >
                <UserItemStyled>
                  <Box mr={{ sm: '500' }}>
                    <AvatarRegularStyled
                      hasImageUrl={imageUrl ? true : false}
                      size={AvatarSize.MEDIUM}
                      src={imageUrl ? imageUrl : undefined}
                    />
                  </Box>
                  <Box flexDirection={{ sm: 'column' }}>
                    <Box pt={{ sm: '100' }} pb={{ sm: '200' }} style={{ whiteSpace: 'nowrap' }}>
                      <Text size="300" color="grey.1000" weight="600">
                        {getFullName(userItem.firstName, userItem.lastName)}
                      </Text>
                    </Box>
                    <Box pb={{ sm: '200' }}>
                      <Text size="200" color="grey.600">
                        {userItem?.crewMember?.persona ? i18n._(PersonaMessages[userItem.crewMember.persona].id) : ''}
                      </Text>
                    </Box>
                    <Text size="200" color="grey.600">
                      {crewNumber}
                    </Text>
                  </Box>
                </UserItemStyled>
              </NavLinkStyled>
            );
          })}
          {!loading && error && (
            <Box
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              flexDirection={{ sm: 'column' }}
              p={{ sm: '500' }}
              data-testid="user-list-retry"
            >
              <ErrorRetry refetch={refetch}>
                <Trans>There was a problem loading users. Please click the button below to try again.</Trans>
              </ErrorRetry>
            </Box>
          )}
          {!loading && !error && userList?.length === 0 && (
            <Box
              flexDirection={{ sm: 'column' }}
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              style={{ width: '100%', height: '100%' }}
              p={{ sm: '500' }}
              data-testid="user-list-empty"
            >
              <Heading size="400" weight="600" color="grey.600">
                <Trans>No users found.</Trans>
              </Heading>
            </Box>
          )}
          {(!called || networkStatus === NetworkStatus.refetch || loading) && (
            <Box
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              p={{ sm: '500' }}
              data-testid="user-list-loading"
            >
              <Spinner size={SpinnerSize.LARGE} />
            </Box>
          )}
        </ListStyled>
      </UserListContainerStyled>
      <ErrorBoundary>
        <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
          {visible && <UserSearchDrawer close={close} initialValues={drawerValues} onSubmit={onDrawerSubmit} />}
        </Suspense>
      </ErrorBoundary>
    </>
  );
};
